import React, { useEffect } from 'react';
import {
  TagPicker,
  TagPickerList,
  TagPickerButton,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  TagPickerInput,
  Tag,
  Avatar,
  Field,
} from '@fluentui/react-components';
import { mapCompanyGroupsStatUserToStatPersonas } from '../../../utils/PersonaHelpers';
import ICompanyGroupStatUsers from '../../../utils/types/ICompanyGroupsStatUsers';
import IPersonaWithGUID from '../../../utils/types/IPersonaWithGUIDs';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';

interface ICompanyGroupUsersPickerProps {
  onInputFieldUpdate?: (inputFieldPersonas: IPersonaWithGUID[]) => void;
  isDisabled?: boolean;
  selectedItems: ICompanyGroupStatUsers[];
  ariaLabelId: string;
}

const CompanyGroupUsersPicker = (props: ICompanyGroupUsersPickerProps): JSX.Element => {
  const { onInputFieldUpdate, isDisabled, selectedItems, ariaLabelId } = props;
  const [currentSelectedItems, setCurrentSelectedItems] = React.useState<IPersonaWithGUID[]>([]);
  const [query, setQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<IPersonaWithGUID[]>([]);

  useEffect(() => {
    if (selectedItems) {
      setCurrentSelectedItems(mapCompanyGroupsStatUserToStatPersonas(selectedItems));
    }
  }, [selectedItems]);

  useEffect(() => {
    const searchUsers = async () => {
      if (query.length > 0) {
        const results = await searchForUsers(query);
        setSearchResults(results as IPersonaWithGUID[]);
      } else {
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchUsers, 200);
    return () => clearTimeout(timeoutId);
  }, [query]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    const selectedGuids = data.selectedOptions;
    const updatedItems = selectedGuids
      .map((guid) => {
        const existing = currentSelectedItems.find((item) => item.graphId === guid);
        if (existing) return existing;
        return searchResults.find((item) => item.graphId === guid);
      })
      .filter(Boolean);

    setCurrentSelectedItems(updatedItems);
    onInputFieldUpdate?.(updatedItems);
    setQuery('');
  };

  const selectedOptionGuids = currentSelectedItems.map((item) => item.graphId);
  const availableOptions = searchResults.filter(
    (option) => !selectedOptionGuids.includes(option.graphId),
  );

  const renderPickerOptions = () => {
    if (availableOptions.length > 0) {
      return availableOptions.map((option) => (
        <TagPickerOption
          secondaryContent={option.secondaryText || ''}
          media={<Avatar aria-hidden name={option.text} color="colorful" />}
          value={option.graphId}
          key={option.graphId}
        >
          {option.text}
        </TagPickerOption>
      ));
    }

    if (query.length > 0) {
      return <TagPickerOption value="no-options">No users found</TagPickerOption>;
    }

    return null;
  };

  return (
    <Field label="Admin Users">
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptionGuids}
        disabled={isDisabled}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label="Selected Admin Users">
            {currentSelectedItems.map((item) => (
              <Tag
                key={item.graphId}
                media={<Avatar aria-hidden name={item.text} color="colorful" />}
                value={item.graphId}
              >
                {item.text}
              </Tag>
            ))}
          </TagPickerGroup>
          <TagPickerInput
            aria-label="Search Users"
            id={ariaLabelId}
            placeholder={isDisabled ? '' : 'Search Users'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <TagPickerButton aria-label="Select Admin Users" />
        </TagPickerControl>

        <TagPickerList>{renderPickerOptions()}</TagPickerList>
      </TagPicker>
    </Field>
  );
};

export default CompanyGroupUsersPicker;
